/**
 * Admin command-center page — redesigned.
 *
 * Reorganized around the admin's actual job: see what's wrong → take
 * action → drill in. Reference layout in design/pages/desktop/CommandCenter.jsx (read,
 * not imported).
 *
 * Top to bottom:
 *   1. StatusBanner       — at-a-glance health pill (tone-coded)
 *   2. IssuesPanel        — conditional list of things to fix
 *   3. MetricsRow         — 7 grouped tiles
 *   4. Profile filter + list, compact rows, 2×2 expansion
 *   5. SystemStatusFooter — SessionCheckerCard + MaintenanceCard
 *   6. Snapshot footer line
 *
 * Driven by one query (`getAdminCommandCenter`) + a BG-jobs poll
 * for session-checker heartbeat. The cleanup-strategy paragraph
 * now lives at /admin/docs/cleanup-strategy; the system-wide
 * Devices table is gone (per-profile expansion is the only place
 * devices appear).
 */
import React, {useMemo, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LaptopMacIcon from '@mui/icons-material/LaptopMac';
import TvIcon from '@mui/icons-material/Tv';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import {
  useAdminDeleteSession,
  useAdminPurgeProfile,
  useAdminSweepProfileSessions,
  useAdminTestInsightNotification,
  useGetAdminCommandCenter,
} from '../../api/graphql_admin_command_center';
import {useAdminRecomputeCorrectiveActions} from '../../api/graphql_corrective_actions';
import {useListBullmqJobs} from '../../api/graphql_failed_tasks';
import {
  AdminCcSessionState,
  AnalysisQueueMessageType,
  BullmqJobState,
  type BullmqJobEntry,
} from '../../types/generated/graphql';
import {useConfirm, useNotify} from '../ui/ConfirmDialog';
import {usePageTitle} from '../../hooks/usePageTitle';
import {pageSx, pageTitleSx} from '../../theme/planapusTheme';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

// --- helpers ----------------------------------------------------------------

/** Direction-aware "human" relative time. Past → "5m ago"; future →
 *  "in 5m". Returns "—" on null/undefined. */
function humanizeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = t - Date.now();
  const ms = Math.abs(diff);
  const past = diff < 0;
  let str: string;
  if (ms < 60_000) str = `${Math.floor(ms / 1000)}s`;
  else if (ms < 3_600_000) str = `${Math.floor(ms / 60_000)}m`;
  else if (ms < 86_400_000) str = `${Math.floor(ms / 3_600_000)}h`;
  else if (ms < 30 * 86_400_000) str = `${Math.floor(ms / 86_400_000)}d`;
  else return new Date(iso).toLocaleDateString();
  return past ? `${str} ago` : `in ${str}`;
}

function humanizeTimeMs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return humanizeTime(new Date(ms).toISOString());
}

// Avatar gradient — pick from a small pool keyed by profileId so the
// same kid always gets the same color across renders.
function avatarGradient(seed: string): string {
  const palettes = [
    'linear-gradient(135deg, #fda4af 0%, #f43f5e 100%)', // rose
    'linear-gradient(135deg, #a5b4fc 0%, #6366f1 100%)', // indigo
    'linear-gradient(135deg, #6ee7b7 0%, #10b981 100%)', // emerald
    'linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)', // amber
    'linear-gradient(135deg, #c4b5fd 0%, #8b5cf6 100%)', // violet
    'linear-gradient(135deg, #67e8f9 0%, #06b6d4 100%)', // cyan
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return palettes[hash % palettes.length];
}

// --- session-checker hook ---------------------------------------------------

interface SessionCheckerStats {
  /** "12s ago" relative string, or null when no tick has ever completed. */
  lastTickAgo: string | null;
  /** Count of recent completed ticks (cap 20). */
  recentTicks: number;
  /** Count of recent failed ticks. */
  recentFailures: number;
  /** True when at least one tick completed within the last 3 minutes. */
  isFresh: boolean;
}

function useSessionCheckerStats(
  client: ApolloClient<NormalizedCacheObject>,
): SessionCheckerStats {
  const {data} = useListBullmqJobs(client, {
    states: [BullmqJobState.Completed, BullmqJobState.Active, BullmqJobState.Failed],
    limit: 50,
  });
  const jobs = data?.listBullmqJobs ?? [];
  const sessionJobs = useMemo(
    () => jobs.filter((j: BullmqJobEntry) => j.name === AnalysisQueueMessageType.ControlHeartbeatSessions),
    [jobs],
  );
  const completed = useMemo(
    () => sessionJobs.filter((j: BullmqJobEntry) => j.state === BullmqJobState.Completed).slice(0, 20),
    [sessionJobs],
  );
  const failed = sessionJobs.filter((j: BullmqJobEntry) => j.state === BullmqJobState.Failed).length;
  const latest = completed[0];
  const msSinceLast = latest?.finishedAt
    ? Date.now() - new Date(latest.finishedAt).getTime()
    : null;
  return {
    lastTickAgo: latest?.finishedAt ? humanizeTime(latest.finishedAt) : null,
    recentTicks: completed.length,
    recentFailures: failed,
    isFresh: msSinceLast != null && msSinceLast < 3 * 60_000,
  };
}

// --- health computation -----------------------------------------------------

type Health = 'healthy' | 'attention' | 'critical';

interface IssueState {
  orphanSessions: number;
  unclaimedProfiles: number;
  signedOut: number;
  expiredSessions: number;
  failedTicks: number;
  staleHeartbeat: boolean;
}

function computeHealth(issues: IssueState): Health {
  if (issues.failedTicks > 0 || issues.staleHeartbeat) return 'critical';
  if (
    issues.orphanSessions > 0 ||
    issues.unclaimedProfiles > 0 ||
    issues.signedOut > 0 ||
    issues.expiredSessions > 0
  ) {
    return 'attention';
  }
  return 'healthy';
}

// --- StatusBanner -----------------------------------------------------------

function StatusBanner({
  health,
  issues,
  generatedAt,
  refreshSeconds,
  onRefresh,
  refreshing,
}: {
  health: Health;
  issues: IssueState;
  generatedAt: string | null | undefined;
  refreshSeconds: number;
  onRefresh: () => void;
  refreshing: boolean;
}): React.JSX.Element {
  const ageSeconds = generatedAt
    ? Math.max(0, Math.floor((Date.now() - new Date(generatedAt).getTime()) / 1000))
    : 0;

  const attentionCount =
    (issues.orphanSessions > 0 ? 1 : 0) +
    (issues.unclaimedProfiles > 0 ? 1 : 0) +
    (issues.signedOut > 0 ? 1 : 0) +
    (issues.expiredSessions > 0 ? 1 : 0);

  const styles =
    health === 'healthy'
      ? {
          bg: '#ecfdf5', // emerald-50
          border: '#a7f3d0', // emerald-200
          icon: <CheckCircleIcon sx={{color: '#059669', fontSize: 22}} />,
          headline: 'All systems healthy',
          body: `Snapshot ${ageSeconds}s old · auto-refreshes every ${refreshSeconds}s.`,
        }
      : health === 'attention'
        ? {
            bg: '#fffbeb', // amber-50
            border: '#fde68a', // amber-200
            icon: <WarningAmberIcon sx={{color: '#d97706', fontSize: 22}} />,
            headline: `${attentionCount} thing${attentionCount === 1 ? '' : 's'} need${attentionCount === 1 ? 's' : ''} attention`,
            body: bannerBody(issues),
          }
        : {
            bg: '#fff1f2', // rose-50
            border: '#fecdd3', // rose-200
            icon: <ErrorOutlineIcon sx={{color: '#e11d48', fontSize: 22}} />,
            headline: 'Critical issue',
            body: issues.failedTicks > 0
              ? `${issues.failedTicks} session-checker tick${issues.failedTicks === 1 ? '' : 's'} failed.`
              : 'Session-checker heartbeat is stale.',
          };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: 2,
        border: `1px solid ${styles.border}`,
        bgcolor: styles.bg,
        px: 2.5,
        py: 2,
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
        {styles.icon}
        <Box>
          <Typography sx={{fontSize: 15, fontWeight: 700, color: '#0f172a'}}>
            {styles.headline}
          </Typography>
          <Typography sx={{fontSize: 12, color: '#475569', mt: 0.25}}>
            {styles.body}
          </Typography>
        </Box>
      </Box>
      <Tooltip title="Refresh snapshot">
        <span>
          <IconButton size="small" onClick={onRefresh} disabled={refreshing}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function bannerBody(issues: IssueState): string {
  const parts: string[] = [];
  if (issues.orphanSessions > 0) parts.push(`${issues.orphanSessions} orphan`);
  if (issues.expiredSessions > 0) parts.push(`${issues.expiredSessions} expired`);
  if (issues.unclaimedProfiles > 0) parts.push(`${issues.unclaimedProfiles} unclaimed`);
  if (issues.signedOut > 0) parts.push(`${issues.signedOut} signed-out`);
  if (parts.length === 0) return 'Everything else is running.';
  return `${parts.join(', ')}. Everything else is running.`;
}

// --- IssuesPanel ------------------------------------------------------------

interface IssueItem {
  severity: 'warn' | 'bad';
  title: string;
  body: string;
  actionLabel: string;
  actionIcon: React.JSX.Element;
  onAction: () => void;
  busy?: boolean;
}

function IssuesPanel({items}: {items: IssueItem[]}): React.JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
      <Typography sx={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
        Needs attention
      </Typography>
      {items.map((item, i) => {
        const tone =
          item.severity === 'bad'
            ? {bg: '#fff1f2', border: '#fecdd3', dot: '#e11d48'}
            : {bg: '#fffbeb', border: '#fde68a', dot: '#f59e0b'};
        return (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              borderRadius: 2,
              border: `1px solid ${tone.border}`,
              bgcolor: tone.bg,
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0}}>
              <Box sx={{mt: 0.75, width: 8, height: 8, borderRadius: '50%', bgcolor: tone.dot, flexShrink: 0}} />
              <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: '#0f172a'}}>{item.title}</Typography>
                <Typography sx={{fontSize: 12, color: '#334155', mt: 0.25, lineHeight: 1.5}}>{item.body}</Typography>
              </Box>
            </Box>
            <Box
              component="button"
              onClick={item.onAction}
              disabled={item.busy}
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: '#0f172a',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                px: 1.5,
                py: 0.875,
                borderRadius: 999,
                border: 0,
                cursor: item.busy ? 'wait' : 'pointer',
                opacity: item.busy ? 0.6 : 1,
                '&:hover': {bgcolor: item.busy ? '#0f172a' : '#1e293b'},
              }}
            >
              {React.cloneElement(item.actionIcon, {sx: {fontSize: 14}})}
              {item.actionLabel}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// --- MetricsRow -------------------------------------------------------------

type Tone = 'neutral' | 'good' | 'warn';

function MetricTile({
  label,
  value,
  sub,
  tone = 'neutral',
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: Tone;
}): React.JSX.Element {
  const styles =
    tone === 'good'
      ? {border: '#a7f3d0', bg: '#fff', valueColor: '#0f172a'}
      : tone === 'warn'
        ? {border: '#fcd34d', bg: 'rgba(254, 243, 199, 0.5)', valueColor: '#b45309'}
        : {border: '#e2e8f0', bg: '#fff', valueColor: '#0f172a'};
  return (
    <Box
      sx={{
        borderRadius: 1.5,
        border: `1px solid ${styles.border}`,
        bgcolor: styles.bg,
        px: 2,
        py: 1.5,
      }}
    >
      <Typography sx={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontSize: 24,
          fontWeight: 800,
          lineHeight: 1,
          color: styles.valueColor,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{mt: 0.5, fontSize: 10, color: '#64748b'}}>{sub}</Typography>
      )}
    </Box>
  );
}

interface MetricsData {
  profiles: number;
  owners: number;
  sessionsTotal: number;
  sessionsActive: number;
  sessionsIdle: number;
  sessionsNew: number;
  expired: number;
  signedOut: number;
  orphan: number;
  devicesOnline: number;
  devicesTotal: number;
}

function MetricsRow({m}: {m: MetricsData}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: 'repeat(7, 1fr)',
        },
      }}
    >
      <MetricTile label="Profiles" value={m.profiles} sub={`${m.owners} owner${m.owners === 1 ? '' : 's'}`} />
      <MetricTile
        label="Sessions"
        value={m.sessionsTotal}
        sub={`${m.sessionsIdle} idle · ${m.sessionsNew} new`}
      />
      <MetricTile label="Active" value={m.sessionsActive} sub="now" />
      <MetricTile
        label="Devices online"
        value={`${m.devicesOnline}/${m.devicesTotal}`}
        sub={m.devicesOnline === m.devicesTotal && m.devicesTotal > 0 ? 'all reporting' : 'partial'}
        tone={m.devicesTotal === 0 ? 'neutral' : m.devicesOnline === m.devicesTotal ? 'good' : 'warn'}
      />
      <MetricTile label="Expired" value={m.expired} tone={m.expired > 0 ? 'warn' : 'neutral'} />
      <MetricTile label="Signed out" value={m.signedOut} tone={m.signedOut > 0 ? 'warn' : 'neutral'} />
      <MetricTile label="Orphan sessions" value={m.orphan} tone={m.orphan > 0 ? 'warn' : 'neutral'} />
    </Box>
  );
}

// --- session state badge (idle = SLATE, not rose) ---------------------------

function SessionStateBadge({state}: {state: AdminCcSessionState}): React.JSX.Element {
  const styles =
    state === AdminCcSessionState.Active
      ? {bg: '#d1fae5', fg: '#047857', label: 'active'}
      : state === AdminCcSessionState.Idle
        ? {bg: '#f1f5f9', fg: '#475569', label: 'idle'}
        : state === AdminCcSessionState.New
          ? {bg: '#e0e7ff', fg: '#4338ca', label: 'new'}
          : state === AdminCcSessionState.Expired
            ? {bg: '#ffe4e6', fg: '#be123c', label: 'expired'}
            : {bg: '#f1f5f9', fg: '#475569', label: String(state).toLowerCase()};
  return (
    <Chip
      size="small"
      label={styles.label}
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        bgcolor: styles.bg,
        color: styles.fg,
      }}
    />
  );
}

// --- ProfileRow -------------------------------------------------------------

type CcProfile = NonNullable<
  NonNullable<ReturnType<typeof useGetAdminCommandCenter>['data']>['getAdminCommandCenter']
>['profiles'][number];

function ProfileRow({
  profile,
  client,
}: {
  profile: CcProfile;
  client: ApolloClient<NormalizedCacheObject>;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [purgeProfile] = useAdminPurgeProfile(client);
  const [deleteSession] = useAdminDeleteSession(client);
  const [testInsight, {loading: testingInsight}] =
    useAdminTestInsightNotification(client);
  const [recompute, {loading: recomputing}] =
    useAdminRecomputeCorrectiveActions(client);
  const confirm = useConfirm();
  const notify = useNotify();

  const youtubeOk = profile.status.youtubeSessionValid === true;
  const devicesOnline = profile.devices.filter(d => d.isActive).length;
  const devicesTotal = profile.devices.length;
  const sessionsTotal = profile.sessions.length;

  const handleDeleteSession = async (sessionId: string): Promise<void> => {
    const ok = await confirm({
      title: `Delete session ${sessionId.slice(0, 16)}…?`,
      message:
        'Removes the session doc and every edge task linked to it. Profile, snapshots, scores etc. are NOT affected. ' +
        'If a desktop is currently using this session, it will need to relink.',
      confirmLabel: 'Delete session',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await deleteSession({variables: {sessionId}});
      const r = res.data?.adminDeleteSession;
      notify({
        severity: r?.errors?.length ? 'warning' : 'success',
        message: r
          ? `Session deleted (${r.tasksDeleted} task${r.tasksDeleted === 1 ? '' : 's'} cascaded)`
          : 'Session delete returned no data.',
      });
    } catch (err) {
      notify({severity: 'error', message: `Delete failed: ${err instanceof Error ? err.message : String(err)}`});
    }
  };

  const handlePurge = async (): Promise<void> => {
    const ok = await confirm({
      title: `Purge profile ${profile.name || profile.profileId}?`,
      message:
        `Permanently deletes the profile doc and EVERY Firestore record keyed to it: ` +
        `sessions, edge tasks, feed/history/related snapshots, corrective actions, feedback, ` +
        `and per-profile caches. Per-video scores in ai_analysis are preserved.\n\n` +
        `This cannot be undone. Profile id: ${profile.profileId}`,
      confirmLabel: 'Purge profile',
      destructive: true,
      requireText: profile.profileId,
    });
    if (!ok) return;
    try {
      const res = await purgeProfile({variables: {profileId: profile.profileId}});
      const r = res.data?.adminPurgeProfile;
      if (!r) {
        notify({severity: 'error', message: 'Purge returned no data.'});
        return;
      }
      const total =
        r.sessionsDeleted +
        r.tasksDeleted +
        r.feedSnapshotsDeleted +
        r.historySnapshotsDeleted +
        r.relatedSnapshotsDeleted +
        r.correctiveActionsDeleted +
        r.feedbackDeleted +
        r.cacheProfileFeedDeleted +
        (r.profileExisted ? 1 : 0) +
        (r.profileStatusDeleted ? 1 : 0);
      notify({
        severity: r.errors.length > 0 ? 'warning' : 'success',
        message:
          r.errors.length > 0
            ? `Purged ${total} docs with ${r.errors.length} error(s): ${r.errors[0]}`
            : `Purged ${total} docs.`,
        durationMs: 10000,
      });
    } catch (err) {
      notify({severity: 'error', message: `Purge failed: ${err instanceof Error ? err.message : String(err)}`});
    }
  };

  const handleTestInsight = async (): Promise<void> => {
    try {
      const res = await testInsight({
        // force a sample if no rated insight is available, so the admin always
        // sees a push land and can confirm the device is reachable.
        variables: {profileId: profile.profileId, force: true},
      });
      const r = res.data?.adminTestInsightNotification;
      if (!r) {
        notify({severity: 'error', message: 'Test insight returned no data.'});
        return;
      }
      const what = r.chosenTitle
        ? `"${r.chosenTitle}" (${r.chosenRating ?? '—'}/${r.chosenSeverity ?? '—'})`
        : 'a sample insight';
      notify({
        severity: r.pushErrors > 0 ? 'warning' : r.pushOk > 0 ? 'success' : 'warning',
        message:
          `Test insight: scanned ${r.scanned}, rated ${r.ratedCount}. ` +
          `Sent ${what} to ${r.pushOk}/${r.devicesTargeted} device(s)` +
          (r.pushErrors > 0 ? `, ${r.pushErrors} error(s).` : '.') +
          ' (dedup ledger NOT written)',
        durationMs: 10000,
      });
    } catch (err) {
      notify({severity: 'error', message: `Test insight failed: ${err instanceof Error ? err.message : String(err)}`});
    }
  };

  const handleRecompute = async (): Promise<void> => {
    try {
      const res = await recompute({variables: {profileId: profile.profileId}});
      const r = res.data?.adminRecomputeCorrectiveActions;
      notify({
        severity: 'success',
        message:
          r?.message ??
          `Re-eval queued: video evaluator + ${r?.channelsMarked ?? 0} channel(s) marked dirty. Sweep runs every 5 min.`,
      });
    } catch (err) {
      notify({severity: 'error', message: `Re-eval failed: ${err instanceof Error ? err.message : String(err)}`});
    }
  };

  const lastTaskAgo = humanizeTime(profile.status.lastTaskAt);
  const initial = (profile.name || '?').charAt(0).toUpperCase();

  return (
    <Box sx={{borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden'}}>
      <Box
        component="button"
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 2,
          px: 2.5,
          py: 1.75,
          textAlign: 'left',
          bgcolor: '#fff',
          border: 0,
          cursor: 'pointer',
          '&:hover': {bgcolor: '#f8fafc'},
        }}
      >
        <Avatar
          src={profile.thumbnailUrl ?? undefined}
          sx={{
            width: 40,
            height: 40,
            fontSize: 14,
            fontWeight: 700,
            background: profile.thumbnailUrl ? undefined : avatarGradient(profile.profileId),
            color: '#fff',
          }}
        >
          {initial}
        </Avatar>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: '#0f172a'}} noWrap>
            {profile.name || '(unnamed)'}
          </Typography>
          <Typography sx={{fontSize: 12, color: '#64748b'}} noWrap>
            {profile.email || '—'}
          </Typography>
        </Box>
        <Box sx={{display: {xs: 'none', md: 'flex'}, alignItems: 'center', gap: 1.5}}>
          <Chip
            size="small"
            label={`${sessionsTotal} session${sessionsTotal === 1 ? '' : 's'}`}
            sx={{height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155'}}
          />
          <Chip
            size="small"
            icon={<TvIcon sx={{fontSize: 12}} />}
            label={`YT ${youtubeOk ? '✓' : '✗'}`}
            sx={{
              height: 22,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: youtubeOk ? '#d1fae5' : '#ffe4e6',
              color: youtubeOk ? '#047857' : '#be123c',
              '& .MuiChip-icon': {color: 'inherit'},
            }}
          />
          <Chip
            size="small"
            label={`${devicesOnline}/${devicesTotal} dev`}
            sx={{height: 22, fontSize: 10, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155'}}
          />
          <Typography sx={{fontSize: 10, color: '#94a3b8', minWidth: 56, textAlign: 'right'}}>
            {profile.status.lastTaskAt ? lastTaskAgo : '—'}
          </Typography>
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{color: '#94a3b8', fontSize: 18}} />
        ) : (
          <KeyboardArrowDownIcon sx={{color: '#94a3b8', fontSize: 18}} />
        )}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{borderTop: '1px solid #f1f5f9', bgcolor: '#f8fafc', px: 2.5, py: 2}}>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {xs: '1fr', lg: 'repeat(2, 1fr)'},
            }}
          >
            {/* Sessions card */}
            <SubCard label="Sessions">
              <Stack spacing={0.75}>
                {profile.sessions.length === 0 ? (
                  <Typography sx={{fontSize: 12, color: '#94a3b8', textAlign: 'center', py: 1}}>
                    No sessions on file.
                  </Typography>
                ) : (
                  profile.sessions.map(s => (
                    <Box
                      key={s.sessionId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        bgcolor: '#f8fafc',
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                      }}
                    >
                      <Tooltip title={s.sessionId}>
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: 11,
                            color: '#334155',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {s.sessionId.slice(0, 18)}…
                        </Typography>
                      </Tooltip>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0}}>
                        <SessionStateBadge state={s.state} />
                        <Typography sx={{fontSize: 10, color: '#94a3b8', minWidth: 48, textAlign: 'right'}}>
                          {humanizeTimeMs(s.lastSeenAt)}
                        </Typography>
                        <Tooltip title="Delete this session and every edge task linked to it. Does NOT touch the profile, snapshots, or scores. If a desktop is using this session it will need to relink.">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSession(s.sessionId)}
                            sx={{color: '#cbd5e1', '&:hover': {color: '#e11d48'}}}
                          >
                            <DeleteOutlineIcon sx={{fontSize: 14}} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))
                )}
              </Stack>
            </SubCard>

            {/* Runtime card */}
            <SubCard label="Runtime">
              <Stack spacing={0.75} sx={{fontSize: 12}}>
                <KvRow
                  label="YouTube valid"
                  value={profile.status.youtubeSessionValid === true ? 'yes' : profile.status.youtubeSessionValid === false ? 'no' : 'unknown'}
                  emphasis={
                    profile.status.youtubeSessionValid === true
                      ? '#047857'
                      : profile.status.youtubeSessionValid === false
                        ? '#be123c'
                        : undefined
                  }
                />
                <KvRow
                  label="YouTube checked"
                  value={humanizeTime(profile.status.youtubeSessionLastCheckedAt)}
                />
                <KvRow
                  label="Last task"
                  value={
                    profile.status.lastTaskAt
                      ? `${profile.status.lastTaskType ?? '?'} · ${humanizeTime(profile.status.lastTaskAt)}`
                      : '—'
                  }
                />
                <KvRow
                  label="Signed out"
                  value={profile.status.youtubeSignedOut ? 'yes' : 'no'}
                  emphasis={profile.status.youtubeSignedOut ? '#be123c' : undefined}
                />
              </Stack>
              {profile.status.youtubeSessionInvalidReason && (
                <Alert severity="warning" variant="outlined" sx={{mt: 1.5, fontSize: 11}}>
                  {profile.status.youtubeSessionInvalidReason}
                </Alert>
              )}
            </SubCard>

            {/* Devices card */}
            <SubCard label="Devices">
              {profile.devices.length === 0 ? (
                <Typography sx={{fontSize: 12, color: '#94a3b8', textAlign: 'center', py: 1}}>
                  No devices subscribed yet.
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {profile.devices.map(d => (
                    <Box
                      key={d.deviceId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#f8fafc',
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.75,
                      }}
                    >
                      {d.type === 'desktop' ? (
                        <LaptopMacIcon sx={{fontSize: 14, color: '#94a3b8'}} />
                      ) : (
                        <PhoneIphoneIcon sx={{fontSize: 14, color: '#94a3b8'}} />
                      )}
                      <Typography sx={{fontSize: 12, fontWeight: 700, color: '#0f172a'}} noWrap>
                        {d.name}
                      </Typography>
                      <Typography sx={{fontSize: 10, color: '#94a3b8'}}>
                        {d.appVersion ? `v${d.appVersion}` : '—'}
                      </Typography>
                      <Box sx={{flex: 1}} />
                      <Chip
                        size="small"
                        label={d.isActive ? 'active' : 'idle'}
                        sx={{
                          height: 18,
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          bgcolor: d.isActive ? '#d1fae5' : '#f1f5f9',
                          color: d.isActive ? '#047857' : '#64748b',
                        }}
                      />
                      <Typography sx={{fontSize: 10, color: '#94a3b8', minWidth: 48, textAlign: 'right'}}>
                        {humanizeTime(d.lastSeenAt)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </SubCard>

            {/* Identity card */}
            <SubCard label="Identity">
              <Stack spacing={1}>
                <IdentityRow label="Profile id" value={profile.profileId} />
                <IdentityRow label="Channel" value={profile.channelHandle ?? '—'} />
                <IdentityRow
                  label="Owner"
                  value={
                    profile.owner
                      ? `${profile.owner.uid}${profile.owner.phoneNumber ? ` · ${profile.owner.phoneNumber}` : ''}`
                      : '—'
                  }
                />
              </Stack>
              <Box sx={{mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                <Tooltip title="Open the full ranked insight candidate list this profile generates — every scanned video with its score breakdown and why it's in that place/order. For auditing the selection algorithm.">
                  <Box
                    component={RouterLink}
                    to={`/admin/operations/profiles/${profile.profileId}/insights`}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#0f766e',
                      bgcolor: 'transparent',
                      border: '1px solid #99f6e4',
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.5,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {bgcolor: '#f0fdfa'},
                    }}
                  >
                    <FormatListNumberedIcon sx={{fontSize: 12}} />
                    Insight list
                  </Box>
                </Tooltip>
                <Tooltip title="Send an insight push to this profile's devices now — bypasses cron/gating/dedup and does NOT mark the card as sent. Forces a sample if nothing is rated.">
                  <Box
                    component="button"
                    onClick={handleTestInsight}
                    disabled={testingInsight}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#1d4ed8',
                      bgcolor: 'transparent',
                      border: '1px solid #bfdbfe',
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.5,
                      cursor: testingInsight ? 'default' : 'pointer',
                      opacity: testingInsight ? 0.6 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {bgcolor: '#eff6ff'},
                    }}
                  >
                    <NotificationsActiveIcon sx={{fontSize: 12}} />
                    {testingInsight ? 'Sending…' : 'Send test insight'}
                  </Box>
                </Tooltip>
                <Tooltip title="Force a re-evaluation of this profile's corrective actions: marks the per-profile video evaluator and every (profile, channel) pair the parent has feedback on as dirty. The 5-min scheduler sweep then re-runs them. Non-destructive — use after changing scoring/agents to refresh verdicts.">
                  <Box
                    component="button"
                    onClick={handleRecompute}
                    disabled={recomputing}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#7c3aed',
                      bgcolor: 'transparent',
                      border: '1px solid #ddd6fe',
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.5,
                      cursor: recomputing ? 'default' : 'pointer',
                      opacity: recomputing ? 0.6 : 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {bgcolor: '#f5f3ff'},
                    }}
                  >
                    <AutorenewIcon sx={{fontSize: 12}} />
                    {recomputing ? 'Queuing…' : 'Re-eval corrective actions'}
                  </Box>
                </Tooltip>
                <Tooltip title="DESTRUCTIVE & irreversible. Deletes the profile doc and EVERY Firestore record keyed to it: sessions, edge tasks, feed/history/related snapshots, corrective actions, parent feedback, and per-profile caches. Per-video AI scores in ai_analysis are preserved (shared across profiles). Requires typing the profile id to confirm.">
                  <Box
                    component="button"
                    onClick={handlePurge}
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#be123c',
                      bgcolor: 'transparent',
                      border: '1px solid #fecdd3',
                      borderRadius: 999,
                      px: 1.25,
                      py: 0.5,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {bgcolor: '#fff1f2'},
                    }}
                  >
                    <DeleteOutlineIcon sx={{fontSize: 12}} />
                    Purge profile…
                  </Box>
                </Tooltip>
              </Box>
            </SubCard>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function SubCard({label, children}: {label: string; children: React.ReactNode}): React.JSX.Element {
  return (
    <Box sx={{borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 1.5}}>
      <Typography sx={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b', mb: 1}}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function KvRow({label, value, emphasis}: {label: string; value: React.ReactNode; emphasis?: string}): React.JSX.Element {
  return (
    <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 1}}>
      <Typography sx={{fontSize: 12, color: '#64748b'}}>{label}</Typography>
      <Typography sx={{fontSize: 12, fontWeight: 600, color: emphasis ?? '#334155', textAlign: 'right'}}>
        {value}
      </Typography>
    </Box>
  );
}

function IdentityRow({label, value}: {label: string; value: string}): React.JSX.Element {
  return (
    <Box>
      <Typography sx={{fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8'}}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'monospace',
          fontSize: 11,
          color: '#334155',
          mt: 0.25,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

// --- SessionCheckerCard -----------------------------------------------------

function SessionCheckerCard({stats}: {stats: SessionCheckerStats}): React.JSX.Element {
  const isWarming = stats.lastTickAgo === null;
  const isHealthy = stats.recentFailures === 0 && stats.isFresh;
  const dotColor = isHealthy ? '#10b981' : isWarming ? '#cbd5e1' : '#e11d48';
  return (
    <Box sx={{borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor}} />
          <Typography sx={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
            Session checker
          </Typography>
        </Box>
        <Typography sx={{fontSize: 10, color: '#94a3b8'}}>BullMQ · every 60s</Typography>
      </Box>
      <Box sx={{mt: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5}}>
        <Box>
          <Typography sx={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
            Last tick
          </Typography>
          <Typography sx={{mt: 0.25, fontSize: 13, fontWeight: 700, color: '#0f172a'}}>
            {stats.lastTickAgo ?? 'Never'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
            Recent ticks
          </Typography>
          <Typography sx={{mt: 0.25, fontSize: 13, fontWeight: 700, color: '#0f172a'}}>
            {stats.recentTicks}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
            Failures
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 13,
              fontWeight: 700,
              color: stats.recentFailures > 0 ? '#be123c' : '#0f172a',
            }}
          >
            {stats.recentFailures}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// --- MaintenanceCard --------------------------------------------------------

function MaintenanceCard({
  orphanCount,
  expiredCount,
  onCleanupOrphans,
  onCleanupExpired,
  onRunSweep,
  busy,
}: {
  orphanCount: number;
  expiredCount: number;
  onCleanupOrphans: () => void;
  onCleanupExpired: () => void;
  onRunSweep: () => void;
  busy: boolean;
}): React.JSX.Element {
  return (
    <Box sx={{borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: 2}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Typography sx={{fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#64748b'}}>
          Maintenance
        </Typography>
        <Box
          component={RouterLink}
          to="/admin/docs/cleanup-strategy"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 10,
            fontWeight: 600,
            color: '#4f46e5',
            textDecoration: 'none',
            '&:hover': {color: '#3730a3'},
          }}
        >
          <HelpOutlineIcon sx={{fontSize: 12}} />
          Cleanup strategy docs
        </Box>
      </Box>
      <Stack spacing={1} sx={{mt: 1.5}}>
        <MaintenanceRow
          title="Orphan sessions"
          body={`${orphanCount} dangling session${orphanCount === 1 ? '' : 's'} · safe to delete`}
          actionLabel="Clean up"
          actionVariant={orphanCount > 0 ? 'primary' : 'disabled'}
          onClick={onCleanupOrphans}
          busy={busy}
          disabled={orphanCount === 0 || busy}
        />
        <MaintenanceRow
          title="Expired sessions"
          body={`${expiredCount} expired · cookies dead`}
          actionLabel={expiredCount > 0 ? 'Clean up' : 'None to clean'}
          actionVariant={expiredCount > 0 ? 'primary' : 'disabled'}
          onClick={onCleanupExpired}
          busy={busy}
          disabled={expiredCount === 0 || busy}
        />
        <MaintenanceRow
          title="Run sweep now"
          body="Keep-most-recent-per-profile (recommended)"
          actionLabel="Run"
          actionVariant="outline"
          onClick={onRunSweep}
          busy={busy}
          disabled={busy}
        />
      </Stack>
    </Box>
  );
}

function MaintenanceRow({
  title,
  body,
  actionLabel,
  actionVariant,
  onClick,
  busy,
  disabled,
}: {
  title: string;
  body: string;
  actionLabel: string;
  actionVariant: 'primary' | 'outline' | 'disabled';
  onClick: () => void;
  busy: boolean;
  disabled: boolean;
}): React.JSX.Element {
  const styles =
    actionVariant === 'primary'
      ? {bg: '#0f172a', color: '#fff', border: 'none', hover: '#1e293b'}
      : actionVariant === 'outline'
        ? {bg: '#fff', color: '#334155', border: '1px solid #e2e8f0', hover: '#f8fafc'}
        : {bg: '#e2e8f0', color: '#94a3b8', border: 'none', hover: '#e2e8f0'};
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#f8fafc',
        borderRadius: 1.5,
        px: 1.5,
        py: 1,
      }}
    >
      <Box>
        <Typography sx={{fontSize: 12, fontWeight: 700, color: '#0f172a'}}>{title}</Typography>
        <Typography sx={{fontSize: 10, color: '#64748b', mt: 0.25}}>{body}</Typography>
      </Box>
      <Box
        component="button"
        onClick={onClick}
        disabled={disabled}
        sx={{
          fontSize: 11,
          fontWeight: 700,
          px: 1.5,
          py: 0.625,
          borderRadius: 999,
          cursor: disabled ? 'not-allowed' : busy ? 'wait' : 'pointer',
          bgcolor: styles.bg,
          color: styles.color,
          border: styles.border,
          opacity: busy ? 0.6 : 1,
          '&:hover': {bgcolor: disabled ? styles.bg : styles.hover},
        }}
      >
        {actionLabel}
      </Box>
    </Box>
  );
}

// --- root component ---------------------------------------------------------

const REFRESH_SECONDS = 10;

export default function AdminCommandCenter({client}: Props): React.JSX.Element {
  usePageTitle('Admin — Command center');
  const [filter, setFilter] = useState('');
  const [maintBusy, setMaintBusy] = useState(false);
  const {data, loading, refetch} = useGetAdminCommandCenter(client);
  const sessionStats = useSessionCheckerStats(client);
  const [deleteSession] = useAdminDeleteSession(client);
  const [sweepProfile] = useAdminSweepProfileSessions(client);
  const confirm = useConfirm();
  const notify = useNotify();

  const cc = data?.getAdminCommandCenter;

  const profiles = useMemo(() => {
    const all = cc?.profiles ?? [];
    if (!filter.trim()) return all;
    const q = filter.toLowerCase();
    return all.filter(
      p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.profileId || '').toLowerCase().includes(q) ||
        (p.owner?.email || '').toLowerCase().includes(q) ||
        (p.owner?.displayName || '').toLowerCase().includes(q),
    );
  }, [cc, filter]);

  const issues: IssueState = {
    orphanSessions: cc?.totals?.orphanSessions ?? 0,
    unclaimedProfiles: cc?.totals?.unclaimedProfiles ?? 0,
    signedOut: cc?.totals?.signedOutProfiles ?? 0,
    expiredSessions: cc?.totals?.expiredSessions ?? 0,
    failedTicks: sessionStats.recentFailures,
    staleHeartbeat: cc != null && !sessionStats.isFresh && sessionStats.lastTickAgo !== null,
  };
  const health = computeHealth(issues);

  const metrics: MetricsData = {
    profiles: cc?.totals?.profiles ?? 0,
    owners: cc?.totals?.ownersWithProfiles ?? 0,
    sessionsTotal: cc?.totals?.sessions ?? 0,
    sessionsActive: cc?.totals?.activeSessions ?? 0,
    sessionsIdle: cc?.totals?.idleSessions ?? 0,
    sessionsNew: cc?.totals?.newSessions ?? 0,
    expired: cc?.totals?.expiredSessions ?? 0,
    signedOut: cc?.totals?.signedOutProfiles ?? 0,
    orphan: cc?.totals?.orphanSessions ?? 0,
    devicesOnline: cc?.totals?.profilesWithActiveDevice ?? 0,
    devicesTotal: cc?.totals?.profiles ?? 0,
  };

  const cleanupOrphans = async (): Promise<void> => {
    if (!cc || cc.orphanSessions.length === 0) return;
    const ok = await confirm({
      title: `Clean up ${cc.orphanSessions.length} orphan session${cc.orphanSessions.length === 1 ? '' : 's'}?`,
      message:
        'Deletes every session row whose profileId no longer resolves. Safe — no code path uses these.',
      confirmLabel: 'Clean up',
      destructive: true,
    });
    if (!ok) return;
    setMaintBusy(true);
    let deleted = 0;
    let errors = 0;
    for (const s of cc.orphanSessions) {
      try {
        await deleteSession({variables: {sessionId: s.sessionId}});
        deleted++;
      } catch {
        errors++;
      }
    }
    setMaintBusy(false);
    notify({
      severity: errors > 0 ? 'warning' : 'success',
      message: `Deleted ${deleted} orphan session${deleted === 1 ? '' : 's'}${errors > 0 ? ` · ${errors} error(s)` : ''}.`,
    });
    await refetch();
  };

  const cleanupExpiredAcrossProfiles = async (
    actionLabel: string,
    confirmTitle: string,
    confirmBody: string,
  ): Promise<void> => {
    if (!cc) return;
    const ok = await confirm({
      title: confirmTitle,
      message: confirmBody,
      confirmLabel: actionLabel,
      destructive: true,
    });
    if (!ok) return;
    setMaintBusy(true);
    let sessionsDeleted = 0;
    let tasksDeleted = 0;
    let errors = 0;
    for (const p of cc.profiles) {
      const hasExpired = p.sessions.some(s => s.state === AdminCcSessionState.Expired);
      if (!hasExpired) continue;
      try {
        const res = await sweepProfile({
          variables: {profileId: p.profileId, states: [AdminCcSessionState.Expired]},
        });
        const r = res.data?.adminSweepProfileSessions;
        if (r) {
          sessionsDeleted += r.sessionsDeleted;
          tasksDeleted += r.tasksDeleted;
        }
      } catch {
        errors++;
      }
    }
    setMaintBusy(false);
    notify({
      severity: errors > 0 ? 'warning' : 'success',
      message: `Swept ${sessionsDeleted} expired session${sessionsDeleted === 1 ? '' : 's'} (${tasksDeleted} task${tasksDeleted === 1 ? '' : 's'} cascaded)${errors > 0 ? ` · ${errors} error(s)` : ''}.`,
    });
    await refetch();
  };

  const cleanupExpired = (): Promise<void> =>
    cleanupExpiredAcrossProfiles(
      'Clean up',
      `Clean up ${metrics.expired} expired session${metrics.expired === 1 ? '' : 's'}?`,
      'Deletes every session whose cookies have expired. Edge tasks bound to those sessions are cascaded.',
    );

  const runSweep = (): Promise<void> =>
    cleanupExpiredAcrossProfiles(
      'Run sweep',
      'Run keep-most-recent-per-profile sweep?',
      'Sweeps expired sessions across every profile (closest available approximation of keep-most-recent-per-profile in the current backend). The most recent session per profile is preserved when its cookies are still valid.',
    );

  return (
    <Box sx={pageSx}>
      <Box sx={{mb: 2}}>
        <Typography sx={pageTitleSx}>Command center</Typography>
        <Typography sx={{fontSize: 13, color: '#64748b', mt: 0.5}}>
          Profiles · sessions · devices — joined in one place. Polls every {REFRESH_SECONDS}s.
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        <StatusBanner
          health={health}
          issues={issues}
          generatedAt={cc?.generatedAt ?? null}
          refreshSeconds={REFRESH_SECONDS}
          onRefresh={() => refetch()}
          refreshing={loading}
        />

        <IssuesPanel
          items={[
            ...(issues.orphanSessions > 0
              ? [
                  {
                    severity: 'warn' as const,
                    title: `${issues.orphanSessions} orphan session${issues.orphanSessions === 1 ? '' : 's'}`,
                    body: "Session(s) without a matching profile. Safe to clean up — no code path uses them.",
                    actionLabel: 'Clean up',
                    actionIcon: <DeleteOutlineIcon />,
                    onAction: () => void cleanupOrphans(),
                    busy: maintBusy,
                  },
                ]
              : []),
            ...(issues.unclaimedProfiles > 0
              ? [
                  {
                    severity: 'warn' as const,
                    title: `${issues.unclaimedProfiles} unclaimed profile${issues.unclaimedProfiles === 1 ? '' : 's'}`,
                    body: 'Profile created but no Firebase user linked.',
                    actionLabel: 'Review',
                    actionIcon: <OpenInNewIcon />,
                    onAction: () => setFilter(''),
                  },
                ]
              : []),
            ...(issues.signedOut > 0
              ? [
                  {
                    severity: 'warn' as const,
                    title: `${issues.signedOut} signed-out profile${issues.signedOut === 1 ? '' : 's'}`,
                    body: 'YouTube returned a signed-out shell on the last task.',
                    actionLabel: 'Review',
                    actionIcon: <OpenInNewIcon />,
                    onAction: () => setFilter(''),
                  },
                ]
              : []),
            ...(issues.failedTicks > 0
              ? [
                  {
                    severity: 'bad' as const,
                    title: `${issues.failedTicks} session-checker tick${issues.failedTicks === 1 ? '' : 's'} failed`,
                    body: 'BullMQ repeatable job had recent failures.',
                    actionLabel: 'View logs',
                    actionIcon: <OpenInNewIcon />,
                    onAction: () => {
                      window.location.href = '/admin/queues';
                    },
                  },
                ]
              : []),
          ]}
        />

        <MetricsRow m={metrics} />

        <Box>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5}}>
            <Typography sx={{fontSize: 18, fontWeight: 800, color: '#0f172a'}}>Profiles</Typography>
            <TextField
              size="small"
              placeholder="Filter by name, email, or id…"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              sx={{width: 280}}
            />
          </Box>
          {loading && !cc ? (
            <Box sx={{p: 4, display: 'flex', justifyContent: 'center'}}>
              <CircularProgress size={24} />
            </Box>
          ) : profiles.length === 0 ? (
            <Paper variant="outlined" sx={{p: 5, textAlign: 'center', borderRadius: 2}}>
              <Typography sx={{fontSize: 13, color: '#64748b'}}>
                {cc ? 'No profiles match.' : 'No data.'}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {profiles.map(p => (
                <ProfileRow key={p.profileId} profile={p} client={client} />
              ))}
            </Stack>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {xs: '1fr', lg: 'repeat(2, 1fr)'},
          }}
        >
          <SessionCheckerCard stats={sessionStats} />
          <MaintenanceCard
            orphanCount={cc?.orphanSessions.length ?? 0}
            expiredCount={metrics.expired}
            onCleanupOrphans={() => void cleanupOrphans()}
            onCleanupExpired={() => void cleanupExpired()}
            onRunSweep={() => void runSweep()}
            busy={maintBusy}
          />
        </Box>

        <Typography sx={{textAlign: 'center', fontSize: 10, color: '#94a3b8'}}>
          Snapshot {cc?.generatedAt ? humanizeTime(cc.generatedAt) : '—'} · auto-refresh every {REFRESH_SECONDS}s
        </Typography>
      </Stack>
    </Box>
  );
}
